import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Timeline, { TimelineHeaders, SidebarHeader, DateHeader, CustomMarker } from 'react-calendar-timeline';
import moment from 'moment';
import 'moment/locale/ko';
import { publicApi } from '../../../utils/publicApi';
import '../../../styles/GanttTimeline.css';
import { PURPLE } from '../../../utils/designUtils';

moment.locale('ko');

const toMoment = (dateVal) => {
    if (!dateVal) return null;
    if (Array.isArray(dateVal)) return moment([dateVal[0], dateVal[1] - 1, dateVal[2]]);
    return moment(dateVal);
};

const getTimestamp = (dateVal) => toMoment(dateVal)?.valueOf() || null;

const buildUserGroupsAndItems = (list) => {
    const groups = [];
    const items = [];
    const userMap = {};

    list.forEach(row => {
        const userKey = row.userEmail;

        if (!userMap[userKey]) {
            userMap[userKey] = true;

            const hasEngineer = row.certificates && row.certificates.includes('정보처리기사') ? '보유' : '미보유';

            groups.push({
                id: userKey,
                title: (
                    <div style={{ display: 'flex', flexDirection: 'column', padding: '8px 0 8px 12px', width: '100%', lineHeight: '1.4' }}>
                        <div style={{ fontWeight: 700, textAlign: 'left', fontSize: 13, color: '#212529' }}>
                            {row.userName}
                        </div>
                        <div style={{ color: '#6c757d', textAlign: 'left', width: '100%', fontSize: 10.5, fontWeight: 'normal' }}>
                            학력 <span style={{ color: '#007bff' }}>{row.eduGrade || '초급'}</span> · 기술 <span style={{ color: '#007bff' }}>{row.techGrade || '초급'}</span>
                        </div>
                        <div style={{ color: '#6c757d', textAlign: 'left', width: '100%', fontSize: 10.5, fontWeight: 'normal' }}>
                            {row.careerDisplay || '경력 없음'} · 정처기 <span style={{ color: hasEngineer === '미보유' ? '#dc3545' : '#007bff' }}>{hasEngineer}</span>
                        </div>
                    </div>
                )
            });
        }

        const planS = getTimestamp(row.startDate);
        const planE = getTimestamp(row.endDate);
        const actS = getTimestamp(row.actualStartDate);
        const actE = getTimestamp(row.actualEndDate);

        if (actS || actE) {
            const realStart = actS || planS;
            const realEnd = actE || planE;
            if (realStart && realEnd && realStart < realEnd) {
                items.push({
                    id: `act_${row.projectInfoId}`,
                    group: userKey,
                    start_time: realStart,
                    end_time: realEnd,
                    title: row.projectName,
                    itemProps: {
                        style: {
                            background: actS && actE ? '#8ABB6C' : '#FF894F',
                            color: '#222', height: '16px', borderRadius: '4px',
                            fontSize: '10px', fontWeight: 700, zIndex: 1
                        }
                    }
                });
            }
        } else if (planS && planE && planS < planE) {
            items.push({
                id: `plan_${row.projectInfoId}`,
                group: userKey,
                start_time: planS,
                end_time: planE,
                title: row.projectName,
                itemProps: {
                    style: {
                        background: '#77BEF0', color: '#fff', height: '16px', borderRadius: '4px',
                        fontSize: '10px', fontWeight: 700, zIndex: 1
                    }
                }
            });
        }
    });

    return { groups, items };
};

const Legend = ({ item }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ backgroundColor: item.color, width: 12, height: 12, marginRight: 5, borderRadius: 2 }} />
        <span style={{ fontSize: 13 }}>{item.text}</span>
    </div>
);

const AlertBox = ({ variant, heading, children }) => {
    const colors = {
        warning: { bg: '#fffbeb', border: '#fcd34d', text: '#92400e' },
        secondary: { bg: '#f8f9fa', border: '#dee2e6', text: '#495057' },
        danger: { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c' },
        info: { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af' },
    };
    const c = colors[variant] || colors.info;
    return (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: c.bg, border: `1px solid ${c.border}`, color: c.text, marginBottom: 16 }}>
            {heading && <div style={{ fontWeight: 700, marginBottom: 4 }}>{heading}</div>}
            {children}
        </div>
    );
};

const SharedGanttPage = () => {
    const { shareId } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userProjectList, setUserProjectList] = useState([]);
    const [sharedEmployeeNames, setSharedEmployeeNames] = useState([]);

    const [zoomMode, setZoomMode] = useState('year');
    const zoomModeRef = useRef('year');
    const [visibleTimeStart, setVisibleTimeStart] = useState(moment().subtract(2, 'years').valueOf());
    const [visibleTimeEnd, setVisibleTimeEnd] = useState(moment().add(2, 'years').valueOf());

    useEffect(() => {
        const fetchShare = async () => {
            setLoading(true);
            setError(null);
            const response = await publicApi.get(`/gantt-shares/${shareId}`);
            if (response.ok) {
                const data = response.data;
                const list =
                    Array.isArray(data) ? data :
                    Array.isArray(data?.ganttData) ? data.ganttData :
                    Array.isArray(data?.userProjectList) ? data.userProjectList :
                    [];
                setUserProjectList(list);
                const nameMap = {};
                list.forEach(row => { nameMap[row.userEmail] = row.userName; });
                setSharedEmployeeNames(Object.values(nameMap));
            } else if (response.status === 404) {
                setError('404');
            } else if (response.status === 410) {
                setError('410');
            } else {
                setError('unknown');
            }
            setLoading(false);
        };
        if (shareId) fetchShare();
    }, [shareId]);

    const handleZoom = useCallback((unit) => {
        setZoomMode(unit);
        zoomModeRef.current = unit;
        const center = (visibleTimeStart + visibleTimeEnd) / 2;
        if (unit === 'year') {
            setVisibleTimeStart(moment(center).subtract(2, 'years').valueOf());
            setVisibleTimeEnd(moment(center).add(2, 'years').valueOf());
        } else {
            setVisibleTimeStart(moment(center).subtract(6, 'months').valueOf());
            setVisibleTimeEnd(moment(center).add(6, 'months').valueOf());
        }
    }, [visibleTimeStart, visibleTimeEnd]);

    const { groups, items } = useMemo(() => buildUserGroupsAndItems(userProjectList), [userProjectList]);

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #e5e0f7', borderTopColor: '#22c55e', animation: 'spin 0.8s linear infinite', marginBottom: 12 }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <p style={{ color: '#7a7889', fontSize: 14 }}>차트를 불러오는 중...</p>
            </div>
        );
    }

    if (error === '404') {
        return (
            <div style={{ padding: '40px 20px' }}>
                <AlertBox variant="warning" heading="링크를 찾을 수 없습니다">
                    <p style={{ margin: 0 }}>공유 링크가 존재하지 않거나 삭제되었습니다.</p>
                </AlertBox>
            </div>
        );
    }

    if (error === '410') {
        return (
            <div style={{ padding: '40px 20px' }}>
                <AlertBox variant="secondary" heading="만료된 링크">
                    <p style={{ margin: 0 }}>이 공유 링크는 만료되었습니다.</p>
                </AlertBox>
            </div>
        );
    }

    if (error === 'unknown') {
        return (
            <div style={{ padding: '40px 20px' }}>
                <AlertBox variant="danger" heading="오류가 발생했습니다">
                    <p style={{ margin: 0 }}>데이터를 불러오는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.</p>
                </AlertBox>
            </div>
        );
    }

    if (userProjectList.length === 0) {
        return (
            <div style={{ padding: '40px 20px' }}>
                <AlertBox variant="info" heading="데이터 없음">
                    <p style={{ margin: 0 }}>이 공유 링크에 표시할 간트차트 데이터가 없습니다.</p>
                </AlertBox>
            </div>
        );
    }

    const zoomBtnStyle = (active) => ({
        height: 30, padding: '0 12px', border: `1.5px solid ${PURPLE}`,
        background: active ? PURPLE : 'white', color: active ? 'white' : PURPLE,
        fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
    });

    return (
        <div style={{ padding: '16px', background: 'white' }}>
            <AlertBox variant="info">
                <span>
                    <strong>공유된 간트차트</strong> — 읽기 전용 뷰입니다.
                    {sharedEmployeeNames.length > 0 && (
                        <span style={{ marginLeft: 8, color: '#6c757d', fontSize: 13 }}>
                            ({sharedEmployeeNames.join(', ')})
                        </span>
                    )}
                </span>
            </AlertBox>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, padding: 12, borderRadius: 8, border: '1px solid #e5e0f7', background: '#faf9fe' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Legend item={{ color: '#8ABB6C', text: '실제기간' }} />
                    <Legend item={{ color: '#FF894F', text: '진행중' }} />
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <div style={{ display: 'flex' }}>
                        <button style={{ ...zoomBtnStyle(zoomMode === 'year'), borderRadius: '6px 0 0 6px', borderRight: 'none' }} onClick={() => handleZoom('year')}>연 단위</button>
                        <button style={{ ...zoomBtnStyle(zoomMode === 'month'), borderRadius: '0 6px 6px 0' }} onClick={() => handleZoom('month')}>월 단위</button>
                    </div>
                    <button
                        style={{ height: 30, padding: '0 12px', borderRadius: 6, border: 'none', background: '#1f1d2e', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
                        onClick={() => {
                            const today = moment();
                            if (zoomModeRef.current === 'year') {
                                setVisibleTimeStart(today.clone().subtract(2, 'years').valueOf());
                                setVisibleTimeEnd(today.clone().add(2, 'years').valueOf());
                            } else {
                                setVisibleTimeStart(today.clone().subtract(6, 'months').valueOf());
                                setVisibleTimeEnd(today.clone().add(6, 'months').valueOf());
                            }
                        }}
                    >
                        Today
                    </button>
                </div>
            </div>

            <div style={{ borderRadius: 8, border: '1px solid #e5e0f7', overflow: 'hidden' }}>
                <Timeline
                    className="user-mode"
                    groups={groups}
                    items={items}
                    stackItems
                    visibleTimeStart={visibleTimeStart}
                    visibleTimeEnd={visibleTimeEnd}
                    onTimeChange={(s, e) => { setVisibleTimeStart(s); setVisibleTimeEnd(e); }}
                    // 이름 아래 부가정보가 2줄로 늘어나 행 높이를 넉넉하게 확보해야 가려지지 않는다
                    lineHeight={82}
                    sidebarWidth={280}
                    canMove={false}
                    canResize={false}
                    groupRenderer={({ group }) => (
                        <div style={{ paddingLeft: 16, paddingTop: 8, paddingBottom: 8 }}>
                            {group.title}
                        </div>
                    )}
                >
                    <TimelineHeaders>
                        <SidebarHeader>
                            {({ getRootProps }) => (
                                <div {...getRootProps()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, background: '#f5f4fa', borderBottom: '1px solid #e5e0f7', borderRight: '1px solid #e5e0f7', color: '#000', width: 280, fontSize: 13 }}>
                                    직원 목록
                                </div>
                            )}
                        </SidebarHeader>
                        <DateHeader unit="primaryHeader" format="YYYY년" />
                        <DateHeader unit="month" labelFormat="M월" />
                    </TimelineHeaders>
                    <CustomMarker date={Date.now()}>
                        {({ styles }) => <div style={{ ...styles, backgroundColor: '#dc3545', width: '2px', zIndex: 100 }} />}
                    </CustomMarker>
                </Timeline>
            </div>
        </div>
    );
};

export default SharedGanttPage;
